import { NodeObject } from "three/src/nodes/TSL.js"
import type MathNode from "three/src/nodes/math/MathNode.js"
import type OperatorNode from "three/src/nodes/math/OperatorNode.js"
import { Fn, hash, instanceIndex, mix, sign, smoothstep, vec3 } from "three/tsl"
import type * as THREE from "three/webgpu"

type ParamNumber = NodeObject<
  | THREE.ConstNode<number>
  | OperatorNode
  | THREE.UniformNode<number>
  | MathNode
  | THREE.Node
>

export function fallOff(
  distance: ParamNumber,
  start: ParamNumber,
  end: ParamNumber,
  margin: ParamNumber,
  progress: ParamNumber,
) {
  const m = margin.mul(sign(end.sub(start))) // margine calcolato per gestire il caso in cui end sia meno di start
  const r = mix(start.sub(m), end, progress)
  return smoothstep(r, r.add(m), distance).oneMinus()
}

/**
 * Hash-based random function for per-particle randomness in shaders.
 * Uses instanceIndex and seedOffset to generate deterministic random values.
 * @param seedOffset - Offset to generate different random values for different purposes
 * @returns A random float value between 0 and 1
 */
export const randomHash = Fn(([seedOffset]: [any]) => {
  return hash(instanceIndex.add(seedOffset))
})

export const hexToRgb = (hex: number) => {
  const r = ((hex >> 16) & 0xff) / 255.0
  const g = ((hex >> 8) & 0xff) / 255.0
  const b = (hex & 0xff) / 255.0
  return vec3(r, g, b)
}

/**
 * Converts a hex color string (e.g., "#ff0000") to normalized RGB values [0-1]
 */
export function hexToRgbNormalized(hex: string): [number, number, number] {
  // Remove # if present
  const cleanHex = hex.replace("#", "")
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255
  return [r, g, b]
}
